/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { codeToHtml } from 'shiki';
import { type ReactNode, useState, useContext, useEffect } from 'react';
import type { CopyState, HighlightCodeProps } from './types';

import { useAsyncEffect } from '../../hooks/useAsyncEffect/useAsyncEffect';
import { formatCode } from '../../utils/formatCode/formatCode';

import './HighlightCode.css';
import { GTMContext } from '../../contexts/GTMContext';
import { GTM_EVENT_COPY_CODE } from '../../constants/GTMEventNames';
import { ExpandLess, ExpandMore, ContentCopyRounded, LibraryAddCheckRounded, Error as ErrorIcon } from '@mui/icons-material';
import { useDimensions } from '../../hooks/useDimensions/useDimensions';

export const CODE_CONTAINER_MAX_HEIGHT = 518;

const COPY_LABEL = 'Copy to clipboard';
const COPIED_LABEL = 'Copied code to clipboard';
const COPY_FAILED_LABEL = 'Copying code failed. Make sure your device is granted permission to clipboard.';

export function HighlightCode( { id, lang, children, onCopyEvent }: HighlightCodeProps ): ReactNode {
	const { triggerGTMEvent } = useContext( GTMContext );

	const [ timerId, setTimerId ] = useState<ReturnType<typeof setTimeout> | null>( null );
	const [ copyState, setCopyState ] = useState<CopyState>( null );
	const [ isTooltipVisible, setIsTooltipVisible ] = useState( false );
	const [ code, setCode ] = useState( '' );
	const [ codeAsHtml, setCodeAsHtml ] = useState( '' );
	const [ isExpanded, setIsExpanded ] = useState( false );
	const [ codeContainer, setCodeContainer ] = useState<HTMLDivElement | null>( null );
	const { height: codeContainerHeight } = useDimensions( codeContainer );
	const shouldDisplayIsExpandButton = codeContainerHeight >= CODE_CONTAINER_MAX_HEIGHT;

	const { error } = useAsyncEffect(
		async () => {
			if ( codeAsHtml ) {
				return;
			}

			const formattedCode = await formatCode( children as string, lang );
			const htmlCode = await codeToHtml( formattedCode.trim(), {
				// Twig supports highlighting importmap.
				lang: lang === 'html' ? 'twig' : lang,
				theme: 'github-dark'
			} );

			setCode( formattedCode );
			setCodeAsHtml( htmlCode );
		},
		() => Promise.resolve(),
		[ codeAsHtml ]
	);

	if ( error ) {
		throw error;
	}

	const onClickCopyHandler = async () => {
		onCopyEvent();

		triggerGTMEvent( {
			event: GTM_EVENT_COPY_CODE,
			option: lang
		} );

		if ( timerId ) {
			clearTimeout( timerId );
		}

		if ( !code ) {
			return;
		}

		try {
			await navigator.clipboard.writeText( code );

			setCopyState( 'active' );
		} catch ( err ) {
			setCopyState( 'failed' );
		} finally {
			setTimerId(
				setTimeout( () => {
					setCopyState( null );
					setTimerId( null );
				}, 3000 )
			);
		}
	};

	useEffect( () => {
		if ( !codeContainer ) {
			return;
		}

		const copyEventHandler = () => {
			triggerGTMEvent( {
				event: GTM_EVENT_COPY_CODE,
				option: lang
			} );

			onCopyEvent();
		};

		codeContainer.addEventListener( 'copy', copyEventHandler );

		return () => {
			codeContainer.removeEventListener( 'copy', copyEventHandler );
		};
	}, [ codeContainer, triggerGTMEvent, lang ] ); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className={ 'snippet-highlight' }>
			<Tooltip title={ getTooltipTitle( copyState ) } data-copy-state={ copyState }>
				<IconButton
					disableRipple={ true }
					className='snippet-highlight__copy-button'
					onClick={ onClickCopyHandler }
					size={ 'medium' }
				>
					{ getCopyIcon( copyState ) }
				</IconButton>
			</Tooltip>

			{ code && (
				<div
					ref={ setCodeContainer }
					className={ `snippet-highlight__code ${ isExpanded ? 'snippet-highlight__code_expanded' : '' }` }
					dangerouslySetInnerHTML={ { __html: codeAsHtml } } id={ id }
				>
				</div>
			) }

			{ shouldDisplayIsExpandButton &&
				<Tooltip
					open={ isTooltipVisible }
					title={ isExpanded ? 'Show less' : 'Show more' }
					onMouseEnter={ () => setIsTooltipVisible( true ) }
					onMouseLeave={ () => setIsTooltipVisible( false ) }
				>
					<IconButton
						disableRipple={ true }
						className='snippet-highlight__show-more-button'
						onClick={ () => {
							setIsExpanded( prevIsExpanded => !prevIsExpanded );
							setIsTooltipVisible( false );
						} }
						size={ 'medium' }
					>
						{ isExpanded ?
							<ExpandLess sx={ { width: '26px', height: '26px' } }/> :
							<ExpandMore sx={ { width: '26px', height: '26px' } }/> }
					</IconButton>
				</Tooltip>
			}
		</div>
	);
}

function getTooltipTitle( copyState: CopyState ) {
	if ( copyState === 'failed' ) {
		return COPY_FAILED_LABEL;
	}

	if ( copyState === 'active' ) {
		return COPIED_LABEL;
	}

	return COPY_LABEL;
}

function getCopyIcon( copyState: CopyState ): ReactNode {
	if ( copyState === 'failed' ) {
		return <ErrorIcon/>;
	}

	if ( copyState === 'active' ) {
		return <LibraryAddCheckRounded/>;
	}

	return <ContentCopyRounded/>;
}
